/**
 * Help section catalogue (E13-F01) — the single ordered source of truth for the
 * in-app user guide.
 *
 * This module drives THREE things at once, which is why it exists separately
 * from the section components:
 *   1. the table of contents (grouped, numbered) rendered by `HelpToc`;
 *   2. the client-side filter behind `HelpSearch` (title + `keywords`);
 *   3. the anchor / `data-testid` contract the e2e suite keys on
 *      (`help-section-<id>` — see docs/user-stories.md → «Справка (Help)»).
 *
 * ── Why keywords instead of full-text search.
 * The section BODIES are hand-written JSX (fixed editorial content — see the
 * spec's §4.2 rationale), so there is no text index to search. Rather than
 * scraping rendered DOM text, each section carries a short keyword list of the
 * synonyms a user would actually type ("mail", "redo", "greyed out"). That
 * covers the requested scenario — «find the right topic by a word» — without an
 * indexing layer nobody asked for (YAGNI).
 *
 * ── Numbering.
 * `welcome` is the page lead: no group, no number, and it is NOT a TOC topic —
 * exactly like the legacy guide, where it sits above the first group divider.
 * The 13 NUMBERED topics (12 inherited from the legacy guide + the new
 * `clients`) are what the TOC lists and what the acceptance criteria count.
 */

/**
 * E13-F02/F03 — the guide is now THREE routed pages (`/help`, `/help_admin`,
 * `/help_research`) sharing this one catalogue instead of three separate ones,
 * so the TOC/search/anchor machinery below is written once. `page` scopes a
 * section to the guide it belongs to; `topicsForPage`/`filterHelpTopicsForPage`
 * are the page-aware counterparts of `HELP_TOPICS`/`filterHelpTopics` (kept
 * below, unchanged, so the original `/help` behaviour/tests are untouched).
 */
export const HELP_PAGE_IDS = ['help', 'help_admin', 'help_research'] as const;
export type HelpPageId = (typeof HELP_PAGE_IDS)[number];

/** TOC group headings, in render order. `welcome` belongs to none of them. */
export const HELP_GROUPS = [
  'Getting started',
  'Running a project',
  'Email',
  'Help',
  // E13-F02 — Admin guide groups.
  'Managing users & teams',
  'Content & reference data',
  // E13-F03 — Research guide groups.
  'Finding companies',
  'The AI chain',
  'Review & publish',
] as const;
export type HelpGroup = (typeof HELP_GROUPS)[number];

export interface HelpSectionMeta {
  /** Stable slug — anchor (`help-section-<id>`) and e2e handle. Never renamed. */
  readonly id: string;
  /** Which routed guide renders this section (`/help`, `/help_admin`, `/help_research`). */
  readonly page: HelpPageId;
  /** TOC group. Absent = the ungrouped lead, rendered above the groups. */
  readonly group?: HelpGroup;
  /** Topic number shown in the TOC / section header. Absent for the lead. */
  readonly number?: number;
  /** Heading text — reused verbatim as the TOC label (one label, one place). */
  readonly title: string;
  /** Lowercase search synonyms; matched as substrings alongside `title`. */
  readonly keywords: readonly string[];
}

/** Every section, in page order. The content column renders exactly this order. */
export const HELP_SECTIONS: readonly HelpSectionMeta[] = [
  {
    id: 'welcome',
    page: 'help',
    title: 'Welcome',
    keywords: ['welcome', 'intro', 'introduction', 'overview', 'guide', 'manual', 'eer app'],
  },
  {
    id: 'login',
    page: 'help',
    group: 'Getting started',
    number: 1,
    title: 'Logging in',
    keywords: ['login', 'log in', 'sign in', 'password', 'email', 'credentials', 'access'],
  },
  {
    id: 'main-screen',
    page: 'help',
    group: 'Getting started',
    number: 2,
    title: 'The main screen',
    keywords: [
      'dashboard',
      'menu',
      'sidebar',
      'navigation',
      'project portfolio',
      'counters',
      'filters',
      'new project',
      'stage',
      'owner',
    ],
  },
  {
    id: 'outlook',
    page: 'help',
    group: 'Getting started',
    number: 3,
    title: 'Connect your Outlook',
    keywords: [
      'outlook',
      'microsoft',
      'mailbox',
      'connect',
      'disconnect',
      'sign-in',
      'mail account',
    ],
  },
  {
    id: 'stages',
    page: 'help',
    group: 'Running a project',
    number: 4,
    title: 'How a project works',
    keywords: [
      'stages',
      'stepper',
      'pre-kickoff',
      'kick-off',
      'generation',
      'launched',
      'workflow',
      'progress',
    ],
  },
  {
    id: 'start',
    page: 'help',
    group: 'Running a project',
    number: 5,
    title: 'Starting a project',
    keywords: [
      'new project',
      'create project',
      'pre-kickoff',
      'add information',
      'position type',
      'ai analysis',
      'upload',
      'links',
      'request details',
      'hybrid',
    ],
  },
  {
    id: 'kickoff',
    page: 'help',
    group: 'Running a project',
    number: 6,
    title: 'The kick-off meeting',
    keywords: [
      'kick-off',
      'kickoff',
      'meeting',
      'questionnaire',
      'questions',
      'meeting notes',
      'transcript',
      'pre-fill',
      'missing data',
      'search company website',
      'company facts',
      'website',
      'web search',
    ],
  },
  {
    id: 'documents',
    page: 'help',
    group: 'Running a project',
    number: 7,
    title: 'Generating documents',
    keywords: [
      'documents',
      'generate',
      'kickoff sheet',
      'job description',
      'inmails',
      'interview questions',
      'sourcing sheet',
      'follow-up email',
      'internal notification',
      'ai instructions',
      'approve',
    ],
  },
  {
    id: 'review',
    page: 'help',
    group: 'Running a project',
    number: 8,
    title: 'Reviewing & editing a document',
    keywords: [
      'review',
      'edit',
      'view',
      'save',
      'approve',
      'send',
      'close',
      'editor',
      'formatting',
      'rich text',
      'bold',
      'italic',
      'toolbar',
      'colour',
      'color',
    ],
  },
  {
    id: 'regenerate',
    page: 'help',
    group: 'Running a project',
    number: 9,
    title: 'Asking the AI to redo a document',
    keywords: [
      'regenerate',
      'redo',
      'rewrite',
      'again',
      'instructions',
      'tone',
      'translate',
      'language',
    ],
  },
  {
    id: 'finish',
    page: 'help',
    group: 'Running a project',
    number: 10,
    title: 'Finishing & launching',
    keywords: [
      'launched',
      'final',
      'confirm',
      'package',
      'delivery',
      'download all',
      'send final package',
      'deliver',
    ],
  },
  {
    id: 'inbox',
    page: 'help',
    group: 'Email',
    number: 11,
    title: 'The Inbox',
    keywords: [
      'inbox',
      'email',
      'mail',
      'check mail',
      'unclassified',
      'linked',
      'reply',
      'link to project',
      'unlink',
    ],
  },
  {
    id: 'clients',
    page: 'help',
    group: 'Email',
    number: 12,
    title: 'Clients',
    keywords: [
      'clients',
      'contacts',
      'directory',
      'address book',
      'duplicates',
      'merge',
      'companies',
    ],
  },
  {
    id: 'tips',
    page: 'help',
    group: 'Help',
    number: 13,
    title: 'Tips & FAQ',
    keywords: [
      'tips',
      'faq',
      'help',
      'troubleshooting',
      'problem',
      'greyed out',
      'stuck',
      'questions',
    ],
  },
  // E13-F02 — Admin guide lead.
  {
    id: 'admin-welcome',
    page: 'help_admin',
    title: 'Admin guide',
    keywords: ['admin', 'settings', 'administration', 'guide'],
  },
  {
    id: 'admin-users',
    page: 'help_admin',
    group: 'Managing users & teams',
    number: 1,
    title: 'Users',
    keywords: ['users', 'accounts', 'role', 'password', 'mailbox', 'imap', 'smtp', 'signature'],
  },
  {
    id: 'admin-sourcing-teams',
    page: 'help_admin',
    group: 'Managing users & teams',
    number: 2,
    title: 'Sourcing Teams',
    keywords: ['sourcing teams', 'sourcers', 'members'],
  },
  {
    id: 'admin-teams',
    page: 'help_admin',
    group: 'Managing users & teams',
    number: 3,
    title: 'Teams',
    keywords: ['teams', 'recruiter teams', 'team lead'],
  },
  {
    id: 'admin-model-matrix',
    page: 'help_admin',
    group: 'Content & reference data',
    number: 4,
    title: 'Model Matrix',
    keywords: [
      'model matrix',
      'prompts',
      'templates',
      'ai model',
      'max tokens',
      'ai providers',
      'anthropic',
      'openai',
      'api key',
      'primary model',
      'fallback model',
      'get actual models',
    ],
  },
  {
    id: 'admin-address-book',
    page: 'help_admin',
    group: 'Content & reference data',
    number: 5,
    title: 'Address Book',
    keywords: ['address book', 'staff', 'contacts', 'category', 'sourcer'],
  },
  {
    id: 'admin-position-types',
    page: 'help_admin',
    group: 'Content & reference data',
    number: 6,
    title: 'Position Types',
    keywords: ['position types', 'additional questions'],
  },
  {
    id: 'admin-questionnaire',
    page: 'help_admin',
    group: 'Content & reference data',
    number: 7,
    title: 'Questionnaire',
    keywords: ['questionnaire', 'base questions', 'kick-off questions', 'ai instruction'],
  },
  {
    id: 'admin-research-settings',
    page: 'help_admin',
    group: 'Content & reference data',
    number: 8,
    title: 'Research settings',
    keywords: [
      'research settings',
      'data-source strategy',
      'zoominfo',
      'review gate',
      'auto-deliver',
    ],
  },
  // E13-F03 — Research guide lead.
  {
    id: 'research-welcome',
    page: 'help_research',
    title: 'Research guide',
    keywords: ['research', 'guide'],
  },
  {
    id: 'research-library',
    page: 'help_research',
    group: 'Finding companies',
    number: 1,
    title: 'Library & history',
    keywords: ['library', 'history', 'cache', 'clone', 'past requests'],
  },
  {
    id: 'research-intake',
    page: 'help_research',
    group: 'Finding companies',
    number: 2,
    title: 'Starting a research request',
    keywords: ['new research request', 'intake', 'parse & autofill', 'target count', 'criteria'],
  },
  {
    id: 'research-run',
    page: 'help_research',
    group: 'Finding companies',
    number: 3,
    title: 'Running discovery',
    keywords: ['run research', 'discovery', 'dedup', 'progress', 'long-list'],
  },
  {
    id: 'research-aichain',
    page: 'help_research',
    group: 'The AI chain',
    number: 4,
    title: 'The AI chain',
    keywords: ['ai check', 'ai chain', 'haiku', 'scrape', 'verify', 'web search', 'company facts'],
  },
  {
    id: 'research-review',
    page: 'help_research',
    group: 'Review & publish',
    number: 5,
    title: 'Reviewing the long-list',
    keywords: ['review', 'accept', 'reject', 'grade', 'evidence', 'gate'],
  },
  {
    id: 'research-publish',
    page: 'help_research',
    group: 'Review & publish',
    number: 6,
    title: 'Publishing results',
    keywords: ['publish', 'sheets', 'export', 'shortlist'],
  },
];

/** The lead section (no group/number) — rendered above the grouped topics. */
export const HELP_LEAD_ID = 'welcome';

/** Lead section id for each page — what `HelpToc`'s "Back to top" scrolls to. */
export const HELP_LEAD_ID_BY_PAGE: Record<HelpPageId, string> = {
  help: 'welcome',
  help_admin: 'admin-welcome',
  help_research: 'research-welcome',
};

/** The numbered topics the TOC lists (everything except the lead). */
export const HELP_TOPICS: readonly HelpSectionMeta[] = HELP_SECTIONS.filter(
  (section) => section.group !== undefined,
);

/** The numbered topics for one page — the page-scoped counterpart of `HELP_TOPICS`. */
export function topicsForPage(page: HelpPageId): readonly HelpSectionMeta[] {
  return HELP_TOPICS.filter((section) => section.page === page);
}

/**
 * Look up a section's metadata by id. Each `sections/*.tsx` file uses this to
 * render its own number/title, so the heading text and the TOC label can never
 * drift apart. An unknown id is a programming error (the catalogue above is
 * static) — it throws, and `SectionErrorBoundary` contains it to the panel.
 */
export function helpSectionMeta(id: string): HelpSectionMeta {
  const found = HELP_SECTIONS.find((section) => section.id === id);
  if (!found) {
    throw new Error(`Unknown help section: ${id}`);
  }
  return found;
}

/** DOM id / `data-testid` of a section container, e.g. `help-section-inbox`. */
export function helpSectionElementId(id: string): string {
  return `help-section-${id}`;
}

/** Case-insensitive substring match over the title and the keyword synonyms. */
export function matchesHelpQuery(section: HelpSectionMeta, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  if (section.title.toLowerCase().includes(needle)) {
    return true;
  }
  return section.keywords.some((keyword) => keyword.includes(needle));
}

/**
 * Filter the TOC topics by a search query. An empty/blank query restores the
 * full list; a query with no hits returns `[]` — the TOC renders its explicit
 * «No matching sections» empty state rather than a blank column.
 */
export function filterHelpTopics(query: string): readonly HelpSectionMeta[] {
  return HELP_TOPICS.filter((section) => matchesHelpQuery(section, query));
}

/** The page-scoped counterpart of `filterHelpTopics` — same rules, one page's topics. */
export function filterHelpTopicsForPage(
  page: HelpPageId,
  query: string,
): readonly HelpSectionMeta[] {
  return topicsForPage(page).filter((section) => matchesHelpQuery(section, query));
}
